import React, { FC, useEffect, useRef, useState } from 'react';
import { Paper, Space, Flex, Tooltip } from '@mantine/core';
import { Button } from '@common/components';
import { Copy } from '@geist-ui/icons';
import { getDomain } from 'src/helpers/getDomain';
import { useAlert } from '@common/hooks';
import { setTooltipTimeout } from 'src/helpers/setTooltipTimeout';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

interface Props {
  siteId: string | undefined;
  tenantId: string | undefined;
}

export const WidgetScript: FC<Props> = ({ siteId, tenantId }) => {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const [scriptTag, setScriptTag] = useState('');
  const domain = getDomain();
  const { showAlert } = useAlert();
  const [t] = useTranslation('widget');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (opened) {
      setTooltipTimeout(2000).then(() => {
        setOpened(false);
      });
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [opened]);

  const handleCopyClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(scriptTag);
      setOpened(true);
    } catch (err) {
      showAlert({
        id: 'copy-error',
        title: t('errorTitle'),
        message: `${t('failedToCopy')}`,
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line max-len
    const scriptString = `<script defer src="${domain}/insight/widget/widget.js" data-ialabs-site="${siteId}" data-ialabs-tenant="${tenantId}" data-ialabs-domain="${domain}"></script>`;
    setScriptTag(scriptString);
  }, [siteId]);

  return (
    <Flex align="flex-end">
      <Paper
        className={styles.script}
        bg="black"
        shadow="none"
        mt={24}
        p="md"
        radius={6}
        h={42}
        ref={widgetContainerRef}>
        {scriptTag}
      </Paper>
      <Space w={24} />
      <Tooltip label={`${t('scriptCopied')}`} opened={opened} withArrow>
        <Flex className={styles.buttonCopy}>
          <Button fullWidth aria-label={`${t('copyScriptLabel')}`} onClick={handleCopyClick}>
            <Copy size={20} aria-label="copy icon" />
          </Button>
        </Flex>
      </Tooltip>
    </Flex>
  );
};
